/**
 * FERP Portal - Document Manager (DRR-074 Tabulator + DRR-079 PDF Pipeline)
 *
 * Combines TabulatorPrimitiveManager pattern (stat cards, Tabulator table, side drawer)
 * with DRR-079 PDF pipeline (viewer, annotations, signing, export).
 *
 * Documents are NOT just CRUD records — they are attached files that need:
 * - PDF rendering with page navigation
 * - Annotation overlays (highlight, text, stamp)
 * - Digital signature capture
 * - Export with flattened annotations
 *
 * FERP Principles:
 * - Dumb Frontend: All document data from backend ListDocuments query
 * - Repository pattern: usePrimitiveList('document') → DocumentRepository
 * - Tabulator: Consistent table UI with sorting, filtering, export
 * - Life-critical: Safety level badges, audit trail display
 */

import { Tabulator } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import '@/styles/tabulator-mantine.css';

import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Button,
  ThemeIcon,
  Paper,
  Badge,
  Loader,
  Center,
  Alert,
  SimpleGrid,
  ActionIcon,
  Menu,
  Drawer,
  Tabs,
  ScrollArea,
  Divider,
  Tooltip,
  Table,
  Modal,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconFileDescription,
  IconRefresh,
  IconDownload,
  IconAlertTriangle,
  IconAlertCircle,
  IconPlugConnectedX,
  IconEye,
  IconFileTypePdf,
  IconSignature,
  IconUsers,
  IconX,
  IconFilePlus,
} from '@tabler/icons-react';
import { SegmentedControl } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useTenantId } from '@/auth';
import { usePrimitiveList, useAdmissionPdfGeneration } from '@/hooks';
import { useDevActorId } from '@/hooks/useDevActorId';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { TABULATOR_DEFAULTS } from '@/utils/tabulatorConfig';
import { useTerminology } from '@ferp/workflow-forms';
import { PDFViewer, SignatureCapture, usePdfExport } from '@/components/pdf';
import type { PdfAnnotation } from '@/components/pdf';
import { useViewTracker } from '@/hooks/useViewTracker';
import { InteractiveStatCard, type StatFilterScope } from '@/components/InteractiveStatCard';
import { formatFieldName, FieldValue, INTERNAL_ID_FIELDS } from '@/components/RecordDetailDrawer';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import {
  DocumentStatus,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  usePdfTemplatesQuery,
  useListInstancesQuery,
} from '@/generated/graphql';

// Use updateDocumentStatus (exists in backend) instead of signDocument (not implemented)
const UPDATE_DOCUMENT_STATUS = gql`
  mutation UpdateDocumentStatus($input: UpdateDocumentStatusInput!) {
    updateDocumentStatus(input: $input) {
      documentId
      currentStatus
      updatedAt
    }
  }
`;

// Pre-signed download URL mutation (MinIO/Wasabi S3)
const REQUEST_DOWNLOAD_URL = gql`
  mutation RequestDocumentDownloadUrl($input: RequestDocumentDownloadUrlInput!) {
    requestDocumentDownloadUrl(input: $input) {
      downloadUrl
      documentName
      mimeType
      expiresInSeconds
    }
  }
`;

// ============================================================================
// TYPES
// ============================================================================

// Tabulator types — use any for cross-version compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabulatorColumn = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabulatorCell = any;

// ============================================================================
// ERROR CLASSIFICATION (same pattern as TabulatorPrimitiveManager)
// ============================================================================

type ErrorType = 'network' | 'validation' | 'server' | 'graphql';

interface ApolloLikeError {
  networkError?: { result?: unknown; statusCode?: number };
  graphQLErrors?: Array<{ extensions?: { code?: string } }>;
}

function isApolloLikeError(error: unknown): error is ApolloLikeError {
  return typeof error === 'object' && error !== null;
}

function classifyError(error: unknown): ErrorType {
  if (!isApolloLikeError(error)) return 'graphql';
  const networkError = error.networkError;
  const graphQLErrors = error.graphQLErrors || [];

  const hasValidationError = graphQLErrors.some(
    (e) => e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED',
  );
  if (hasValidationError) return 'validation';
  if (networkError && !networkError.result && !networkError.statusCode) return 'network';
  if (networkError?.statusCode && networkError.statusCode >= 500) return 'server';
  return 'graphql';
}

const ERROR_DISPLAY: Record<ErrorType, { title: string; color: string; icon: typeof IconAlertTriangle }> = {
  network: { title: 'Backend Unavailable', color: 'orange', icon: IconPlugConnectedX },
  validation: { title: 'Query Configuration Error', color: 'yellow', icon: IconAlertCircle },
  server: { title: 'Server Error', color: 'red', icon: IconAlertTriangle },
  graphql: { title: 'Error Loading Data', color: 'red', icon: IconAlertTriangle },
};

const ERROR_MESSAGES: Record<ErrorType, string> = {
  network: 'Could not connect to the FERP Core backend. The service may be starting up or temporarily unavailable.',
  validation: 'The GraphQL query for documents has a schema mismatch. This is a frontend configuration issue.',
  server: 'The backend returned a server error. Check gateway logs for details.',
  graphql: '',
};

// ============================================================================
// BADGE MAPS (document-specific)
// ============================================================================

const STATUS_BADGE_MAP: Record<string, string> = {
  DRAFT: 'builtin',
  PENDING_REVIEW: 'high',
  APPROVED: 'low',
  PUBLISHED: 'low',
  ARCHIVED: 'normal',
  REJECTED: 'critical',
};

const STATUS_COLOR_MAP: Record<string, string> = {
  DRAFT: 'gray',
  PENDING_REVIEW: 'yellow',
  APPROVED: 'green',
  PUBLISHED: 'blue',
  ARCHIVED: 'gray',
  REJECTED: 'red',
};

const DOC_TYPE_BADGE_MAP: Record<string, string> = {
  WorkflowDefinition: 'builtin',
  PolicyDocument: 'high',
  ComplianceReport: 'urgent',
  AuditReport: 'critical',
  Template: 'normal',
  Attachment: 'low',
};

// ============================================================================
// STAT FILTER TYPE
// ============================================================================

type StatFilterCategory = 'total' | 'pdfs' | 'drafts' | 'approved';

interface StatFilter {
  category: StatFilterCategory;
  scope: StatFilterScope;
}

// InteractiveStatCard + StatFilterScope imported from @/components/InteractiveStatCard (DRY)

// ============================================================================
// HELPERS
// ============================================================================

function formatFileSize(bytes: string | number | null | undefined): string {
  const n = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (!n || isNaN(n)) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function isPdf(item: PrimitiveItem): boolean {
  return item.mimeType === 'application/pdf';
}

// ============================================================================
// DOCUMENT-SPECIFIC COLUMNS
// ============================================================================

const getDocumentColumns = (): TabulatorColumn[] => [
  {
    title: 'Document',
    field: 'documentName',
    sorter: 'string',
    headerFilter: 'input',
    headerFilterPlaceholder: 'Search documents...',
    formatter: (cell: TabulatorCell) => {
      const row = cell.getRow().getData();
      const name = cell.getValue() ?? row.name ?? '';
      const mime = row.mimeType ?? '';
      const pdfBadge = mime === 'application/pdf'
        ? ' <span class="ferp-tbl-badge ferp-tbl-badge--urgent" style="font-size:10px;">PDF</span>'
        : '';
      return `${name}${pdfBadge}`;
    },
  },
  {
    title: 'Type',
    field: 'documentType',
    width: 150,
    sorter: 'string',
    headerFilter: 'input',
    headerFilterPlaceholder: 'Filter type...',
    formatter: (cell: TabulatorCell) => {
      const val = cell.getValue() as string;
      if (!val) return '';
      const cssClass = DOC_TYPE_BADGE_MAP[val] ?? 'normal';
      return `<span class="ferp-tbl-badge ferp-tbl-badge--${cssClass}">${val}</span>`;
    },
  },
  {
    title: 'Category',
    field: 'category',
    width: 120,
    sorter: 'string',
    headerFilter: 'input',
    headerFilterPlaceholder: 'Filter category...',
  },
  {
    title: 'Size',
    field: 'fileSizeBytes',
    width: 100,
    sorter: 'number',
    formatter: (cell: TabulatorCell) => formatFileSize(cell.getValue()),
  },
  {
    title: 'Status',
    field: 'currentStatus',
    width: 140,
    sorter: 'string',
    headerFilter: 'list',
    headerFilterParams: {
      values: { '': 'All', DRAFT: 'Draft', PENDING_REVIEW: 'Pending Review', APPROVED: 'Approved', PUBLISHED: 'Published', ARCHIVED: 'Archived', REJECTED: 'Rejected' },
    },
    headerFilterPlaceholder: 'Filter status...',
    formatter: (cell: TabulatorCell) => {
      const val = cell.getValue() as string;
      if (!val) return '';
      const cssClass = STATUS_BADGE_MAP[val] ?? 'normal';
      const label = val.replace(/_/g, ' ');
      return `<span class="ferp-tbl-badge ferp-tbl-badge--${cssClass}">${label}</span>`;
    },
  },
  {
    title: 'Regulatory',
    field: 'regulatoryRuleIds',
    width: 120,
    sorter: (a: string[] | null, b: string[] | null) => (a?.length ?? 0) - (b?.length ?? 0),
    headerFilter: 'list' as const,
    headerFilterParams: {
      values: { '': 'All', 'has-rules': 'Has Rules', 'no-rules': 'No Rules' },
    },
    headerFilterFunc: (headerValue: string, rowValue: string[] | null) => {
      if (!headerValue) return true;
      if (headerValue === 'has-rules') return (rowValue?.length ?? 0) > 0;
      return (rowValue?.length ?? 0) === 0;
    },
    formatter: (cell: TabulatorCell) => {
      const rules = (cell.getValue() as string[] | null) ?? [];
      if (rules.length === 0) return '<span style="color:var(--mantine-color-gray-5)">—</span>';
      return `<span class="ferp-tbl-badge ferp-tbl-badge--urgent">${rules.length} rule${rules.length > 1 ? 's' : ''}</span>`;
    },
  },
  {
    title: 'Version',
    field: 'version',
    width: 80,
    hozAlign: 'center',
    sorter: 'string',
    formatter: (cell: TabulatorCell) => {
      const val = cell.getValue();
      return val ? `v${val}` : '';
    },
  },
  {
    title: 'Updated',
    field: 'updatedAt',
    width: 130,
    sorter: 'string',
    formatter: (cell: TabulatorCell) => {
      const val = cell.getValue();
      if (!val) return '';
      return new Date(val as string).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    },
  },
  {
    title: 'Actions',
    field: '_actions',
    width: 160,
    hozAlign: 'center' as const,
    headerSort: false,
    formatter: (_cell: TabulatorCell) => {
      const row = _cell.getRow().getData();
      const pdfBtn = row.mimeType === 'application/pdf'
        ? '<button class="ferp-tbl-action ferp-tbl-action--primary tabulator-btn-pdf" title="View PDF">PDF</button>'
        : '';
      return `<button class="ferp-tbl-action ferp-tbl-action--secondary tabulator-btn-view" title="View">View</button>${pdfBtn}`;
    },
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

type DocFilter = 'all' | 'my';

export function DocumentManager() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const navigate = useNavigate();
  const skip = !tenantId;
  const { actorId, actorName, loading: actorLoading } = useDevActorId();
  const [docFilter, setDocFilter] = useState<DocFilter>('all');

  // Data loading via repository pattern
  const { items, loading, error, refetch } = usePrimitiveList('document');

  // Per-actor filtering: "My Documents" filters by createdBy === actorId
  // NOTE: Seeded documents have createdBy="seed-script", so this will show 0 until
  // real documents are created via mutations with proper auth context.
  const data: PrimitiveItem[] = useMemo(() => {
    const allItems = items ?? [];
    if (docFilter !== 'my' || !actorId) return allItems;
    return allItems.filter((d) => (d as Record<string, unknown>).createdBy === actorId);
  }, [items, docFilter, actorId]);

  // Tabulator
  const tableRef = useRef<HTMLDivElement>(null);
  const tabulatorInstance = useRef<any | null>(null);
  const tableBuiltRef = useRef(false);
  // dataRef lets the tableBuilt callback load the initial snapshot without being in Effect 1 deps
  const dataRef = useRef<PrimitiveItem[]>(data);

  // Detail drawer state
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState<PrimitiveItem | null>(null);
  const [drawerTab, setDrawerTab] = useState<string | null>('details');

  // PDF viewer state (DRR-079)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [annotations, setAnnotations] = useState<PdfAnnotation[]>([]);
  const [signatureOpened, { open: openSignature, close: closeSignature }] = useDisclosure(false);

  // PDF pipeline — use updateDocumentStatus for signing (backend mutation exists)
  // Annotations are kept in-memory only (savePdfAnnotations not yet in backend)
  const [updateDocumentStatus] = useMutation(UPDATE_DOCUMENT_STATUS);

  // DRR-125: Document CRUD mutations (feature surfacing)
  // createDocument: For programmatic document creation (upload flow uses separate presigned URL path)
  // deleteDocument: Soft delete for append-only compliance (DEPRECATED - use archiveDocument)
  const [createDocument] = useCreateDocumentMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  // Mark as available for future action wiring
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _documentMutations = { createDocument, deleteDocument };

  // DRR-232 Track I: PDF generation hook (wired from useDocumentGeneration)
  const { generateAdmissionPdf, loading: generatingPdf, error: pdfError } = useAdmissionPdfGeneration();

  // PDF Generation Modal state
  const [pdfModalOpened, { open: openPdfModal, close: closePdfModal }] = useDisclosure(false);
  const [pdfFormState, setPdfFormState] = useState({
    templateId: '',
    documentName: '',
    workflowInstanceId: '',
    dataContextJson: '{}',
    regulatoryRuleIds: [] as string[],
  });

  // Queries for PDF generation modal dropdowns
  const { data: templatesData } = usePdfTemplatesQuery({
    variables: { subgraph: 'fhir' },
    skip: !pdfModalOpened,
  });
  const { data: instancesData } = useListInstancesQuery({
    variables: { tenantId, limit: 50 },
    skip: !pdfModalOpened || !tenantId,
  });

  // Template options for Select
  const templateOptions = useMemo(() => {
    return (templatesData?.pdfTemplates ?? []).map((t) => ({
      value: t.templateId,
      label: `${t.name}${t.description ? ` — ${t.description}` : ''}`,
    }));
  }, [templatesData]);

  // Instance options for Select
  const instanceOptions = useMemo(() => {
    return (instancesData?.instanceApiListInstances?.items ?? []).map((i) => ({
      value: i.id,
      label: `${i.workflowName ?? i.workflowDefinitionId} — ${i.currentState}`,
    }));
  }, [instancesData]);

  // Common regulatory rule options (hardcoded for now, could come from backend)
  const regulatoryRuleOptions = [
    { value: 'rl-4-0-009', label: 'rl-4-0-009 — Admission Agreement' },
    { value: 'rl-4-0-012', label: 'rl-4-0-012 — Rights Notification' },
    { value: 'rl-4-0-146', label: 'rl-4-0-146 — Care Plan Documentation' },
  ];

  // Handle PDF generation submit
  const handleGeneratePdf = async () => {
    if (!pdfFormState.templateId || !pdfFormState.documentName || !pdfFormState.workflowInstanceId) {
      return;
    }
    try {
      const result = await generateAdmissionPdf({
        tenantId,
        templateId: pdfFormState.templateId,
        documentName: pdfFormState.documentName,
        workflowInstanceId: pdfFormState.workflowInstanceId,
        dataContextJson: pdfFormState.dataContextJson || '{}',
        regulatoryRuleIds: pdfFormState.regulatoryRuleIds.length > 0 ? pdfFormState.regulatoryRuleIds : undefined,
      });
      if (result?.success) {
        closePdfModal();
        // Reset form
        setPdfFormState({
          templateId: '',
          documentName: '',
          workflowInstanceId: '',
          dataContextJson: '{}',
          regulatoryRuleIds: [],
        });
        // Refetch documents to show new one
        refetch();
      }
    } catch (err) {
      console.error('PDF generation failed:', err);
    }
  };

  // View tracker for "new" / unviewed documents — destructure stable callbacks directly
  // (returning the whole object gives a new reference every render, destabilizing handlers)
  const { markViewed: markDocViewed, isViewed: isDocViewed } = useViewTracker('document');

  // Active stat filter
  const [activeFilter, setActiveFilter] = useState<StatFilter | null>(null);

  // ============================================================================
  // COMPUTED STATS (total + new/unviewed counts)
  // ============================================================================

  const stats = useMemo(() => {
    const totalCount = data.length;
    const pdfCount = data.filter((d) => d.mimeType === 'application/pdf').length;
    const draftCount = data.filter((d) => d.currentStatus === DocumentStatus.DRAFT).length;
    const approvedCount = data.filter((d) =>
      d.currentStatus === DocumentStatus.APPROVED || d.currentStatus === DocumentStatus.PUBLISHED,
    ).length;

    const newTotalCount = data.filter((d) => !isDocViewed(d.documentId as string)).length;
    const newPdfCount = data.filter((d) => d.mimeType === 'application/pdf' && !isDocViewed(d.documentId as string)).length;
    const newDraftCount = data.filter((d) => d.currentStatus === DocumentStatus.DRAFT && !isDocViewed(d.documentId as string)).length;
    const newApprovedCount = data.filter((d) =>
      (d.currentStatus === DocumentStatus.APPROVED || d.currentStatus === DocumentStatus.PUBLISHED) &&
      !isDocViewed(d.documentId as string),
    ).length;

    return {
      totalCount, pdfCount, draftCount, approvedCount,
      newTotalCount, newPdfCount, newDraftCount, newApprovedCount,
    };
  }, [data, isDocViewed]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleView = useCallback((item: PrimitiveItem) => {
    markDocViewed(item.documentId as string);
    setSelectedItem(item);
    setDrawerTab('details');
    setPdfViewerOpen(false);
    openDrawer();
  }, [openDrawer, markDocViewed]);

  const handleOpenPdf = useCallback((item: PrimitiveItem) => {
    markDocViewed(item.documentId as string);
    setSelectedItem(item);
    setDrawerTab('pdf');
    setPdfViewerOpen(true);
    // Load saved annotations
    if (item.dynamodbContent) {
      try {
        const parsed = JSON.parse(item.dynamodbContent as string);
        setAnnotations(Array.isArray(parsed) ? parsed : []);
      } catch {
        setAnnotations([]);
      }
    } else {
      setAnnotations([]);
    }
    openDrawer();
  }, [openDrawer, markDocViewed]);

  const handleAnnotationsChange = useCallback((newAnnotations: PdfAnnotation[]) => {
    // Annotations kept in-memory for this session (no backend mutation yet)
    setAnnotations(newAnnotations);
  }, []);

  const handleSign = useCallback((_signatureDataUrl: string, _signerId: string) => {
    if (!selectedItem) return;
    const docId = (selectedItem.documentId as string) ?? (selectedItem.id as string);

    // Use updateDocumentStatus to mark as APPROVED (signing = approval in DRR-079)
    updateDocumentStatus({
      variables: {
        input: {
          documentId: docId,
          newStatus: 'APPROVED',
          reason: `Signed by ${_signerId}`,
        },
      },
      onCompleted: () => refetch(),
      onError: (err: { message: string }) => console.error('[DocumentManager] Sign failed:', err.message),
    });
    closeSignature();
  }, [selectedItem, updateDocumentStatus, closeSignature, refetch]);

  const handleExport = useCallback(
    (format: 'csv' | 'json') => {
      const timestamp = new Date().toISOString().split('T')[0];
      tabulatorInstance.current?.download(format, `documents_${timestamp}.${format}`);
    },
    [],
  );

  // PDF URL via presigned S3 download URL
  const docId = selectedItem?.documentId ?? selectedItem?.id ?? '';
  const [pdfUrl, setPdfUrl] = useState('');
  const [requestDownloadUrl] = useMutation(REQUEST_DOWNLOAD_URL);

  useEffect(() => {
    if (!pdfViewerOpen || !docId) {
      setPdfUrl('');
      return;
    }
    requestDownloadUrl({
      variables: { input: { documentId: docId } },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).then((result: any) => {
      const url = result.data?.requestDocumentDownloadUrl?.downloadUrl;
      if (url) setPdfUrl(url);
    }).catch((err) => {
      console.error('[DocumentManager] Failed to get download URL:', err);
      // Fallback to Vite dev server for local dev without MinIO
      setPdfUrl(`/api/documents/${docId}/download`);
    });
  }, [pdfViewerOpen, docId, requestDownloadUrl]);

  const { exportPdf } = usePdfExport(
    pdfUrl,
    annotations,
    (selectedItem?.documentName as string) ?? (selectedItem?.name as string) ?? 'document',
  );

  // ============================================================================
  // TABULATOR INIT — Effect 1 (structure) + Effect 2 (data)
  // Same two-effect pattern as TabulatorPrimitiveManager: structure changes
  // destroy/recreate the table; data changes use replaceData to avoid flashing.
  // ============================================================================

  // Refs so cellClick listener always calls latest handlers without being in Effect 1 deps
  const handleViewRef = useRef(handleView);
  handleViewRef.current = handleView;
  const handleOpenPdfRef = useRef(handleOpenPdf);
  handleOpenPdfRef.current = handleOpenPdf;
  // Keep dataRef current so tableBuilt callback can load the initial data snapshot
  dataRef.current = data;

  // Effect 1: Create table (structure only — no data deps, no handler deps)
  useEffect(() => {
    if (!tableRef.current) return;

    try {
      tabulatorInstance.current?.destroy();
    } catch {
      // safe to ignore
    }
    tabulatorInstance.current = null;

    try {
      tableBuiltRef.current = false;
      tabulatorInstance.current = new Tabulator(tableRef.current, {
        ...TABULATOR_DEFAULTS,
        data: [],
        columns: getDocumentColumns(),
        placeholder: 'No documents found',
        initialSort: [{ column: 'updatedAt', dir: 'desc' }],
      });

      tabulatorInstance.current.on('tableBuilt', () => {
        tableBuiltRef.current = true;
        // Load any data that arrived before tableBuilt fired (Apollo cache hit race condition)
        if (dataRef.current.length > 0) {
          try {
            tabulatorInstance.current?.replaceData(structuredClone(dataRef.current));
          } catch { /* safe */ }
        }
      });

      tabulatorInstance.current.on('cellClick', (e: Event, cell: TabulatorCell) => {
        const field = cell.getColumn().getField();
        const item = cell.getRow().getData() as PrimitiveItem;

        if (field === '_actions') {
          const target = e.target as HTMLElement;
          if (target.classList.contains('tabulator-btn-view')) handleViewRef.current(item);
          if (target.classList.contains('tabulator-btn-pdf')) handleOpenPdfRef.current(item);
        } else {
          if (isPdf(item)) {
            handleOpenPdfRef.current(item);
          } else {
            handleViewRef.current(item);
          }
        }
      });

      tabulatorInstance.current.on('rowMouseEnter', (_: unknown, row: { getElement: () => HTMLElement }) => {
        row.getElement().style.cursor = 'pointer';
      });
    } catch (err) {
      console.error('[DocumentManager] Tabulator init failed:', err);
    }

    return () => {
      tableBuiltRef.current = false;
      try { tabulatorInstance.current?.destroy(); } catch { /* safe */ }
      tabulatorInstance.current = null;
    };
    // No data/handler deps — handlers accessed via refs, data loaded via Effect 2
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect 2: Replace data without destroying the table
  useEffect(() => {
    if (tabulatorInstance.current && tableBuiltRef.current && data) {
      try {
        tabulatorInstance.current.replaceData(structuredClone(data));
      } catch {
        // Safe to ignore — table may not be fully initialized yet
      }
    }
  }, [data]);

  // ============================================================================
  // STAT CARD FILTER APPLICATION
  // ============================================================================

  useEffect(() => {
    if (!tabulatorInstance.current || !tableBuiltRef.current) return;

    if (!activeFilter) {
      tabulatorInstance.current.clearFilter();
      return;
    }

    // Use custom filter function to support both category + scope (new/all)
    tabulatorInstance.current.setFilter((rowData: PrimitiveItem) => {
      // Category match
      let matchesCategory = true;
      switch (activeFilter.category) {
        case 'pdfs':
          matchesCategory = rowData.mimeType === 'application/pdf';
          break;
        case 'drafts':
          matchesCategory = rowData.currentStatus === DocumentStatus.DRAFT;
          break;
        case 'approved':
          matchesCategory = rowData.currentStatus === DocumentStatus.APPROVED || rowData.currentStatus === DocumentStatus.PUBLISHED;
          break;
        case 'total':
        default:
          matchesCategory = true;
          break;
      }

      // Scope match
      if (activeFilter.scope === 'new') {
        return matchesCategory && !isDocViewed(rowData.documentId as string);
      }

      return matchesCategory;
    });
  }, [activeFilter, isDocViewed]);

  // ============================================================================
  // STAT CARD CLICK HANDLERS
  // ============================================================================

  const handleStatClick = useCallback((category: StatFilterCategory, scope: StatFilterScope) => {
    setActiveFilter((prev) => {
      // Toggle off if clicking the same filter
      if (prev && prev.category === category && prev.scope === scope) return null;
      return { category, scope };
    });
  }, []);

  // ============================================================================
  // ERROR STATES
  // ============================================================================

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view documents.
        </Alert>
      </Container>
    );
  }

  if (error && !data.length) {
    const errorType = classifyError(error);
    const display = ERROR_DISPLAY[errorType];
    const ErrorIcon = display.icon;

    return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="cyan">
              <IconFileDescription size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>{translate('document')}s</Title>
              <Text c="dimmed" size="sm">Workflow documents and attachments</Text>
            </div>
          </Group>
          <Alert icon={<ErrorIcon size={16} />} color={display.color} title={display.title}>
            <Stack gap="sm">
              <Text size="sm">{ERROR_MESSAGES[errorType] || error.message}</Text>
              <Button leftSection={<IconRefresh size={16} />} variant="light" size="sm" onClick={() => refetch()} style={{ alignSelf: 'flex-start' }}>
                Retry
              </Button>
            </Stack>
          </Alert>
        </Stack>
      </Container>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <Container fluid py="xl" px="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="cyan">
              <IconFileDescription size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>{translate('document')}s</Title>
              <Text c="dimmed" size="sm">Workflow documents, PDFs, and attachments</Text>
            </div>
          </Group>

          <Group gap="sm">
            <Button leftSection={<IconRefresh size={16} />} variant="light" onClick={() => refetch()} loading={loading}>
              Refresh
            </Button>
            <Tooltip label="Generate admission PDF from template (DRR-232)">
              <Button
                leftSection={<IconFilePlus size={16} />}
                variant="filled"
                loading={generatingPdf}
                onClick={openPdfModal}
              >
                Generate PDF
              </Button>
            </Tooltip>
          </Group>
        </Group>

        {/* Per-actor filter: All Documents / My Documents */}
        <Group gap="md">
          <SegmentedControl
            size="xs"
            value={docFilter}
            onChange={(value) => setDocFilter(value as DocFilter)}
            data={[
              { label: 'All Documents', value: 'all' },
              { label: 'My Documents', value: 'my' },
            ]}
          />
          {docFilter === 'my' && (
            <Group gap="xs">
              {actorLoading ? (
                <Loader size="xs" />
              ) : actorId ? (
                <Badge variant="light" color="blue" size="sm">
                  {actorName}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  No actor found for current user
                </Text>
              )}
            </Group>
          )}
        </Group>

        {/* Partial error banner */}
        {error && data.length > 0 && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Partial Data">
            Some document data may be incomplete. {error.message}
          </Alert>
        )}

        {/* Interactive Stat Cards */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <InteractiveStatCard
            label="Total"
            total={stats.totalCount}
            newCount={stats.newTotalCount}
            color="cyan"
            isActive={activeFilter?.category === 'total'}
            activeScope={activeFilter?.category === 'total' ? activeFilter.scope : null}
            onClickAll={() => handleStatClick('total', 'all')}
            onClickNew={() => handleStatClick('total', 'new')}
          />
          <InteractiveStatCard
            label="PDFs"
            total={stats.pdfCount}
            newCount={stats.newPdfCount}
            color="violet"
            icon={<IconFileTypePdf size={18} color="var(--mantine-color-violet-5)" />}
            isActive={activeFilter?.category === 'pdfs'}
            activeScope={activeFilter?.category === 'pdfs' ? activeFilter.scope : null}
            onClickAll={() => handleStatClick('pdfs', 'all')}
            onClickNew={() => handleStatClick('pdfs', 'new')}
          />
          <InteractiveStatCard
            label="Drafts"
            total={stats.draftCount}
            newCount={stats.newDraftCount}
            color="yellow"
            isActive={activeFilter?.category === 'drafts'}
            activeScope={activeFilter?.category === 'drafts' ? activeFilter.scope : null}
            onClickAll={() => handleStatClick('drafts', 'all')}
            onClickNew={() => handleStatClick('drafts', 'new')}
          />
          <InteractiveStatCard
            label="Approved"
            total={stats.approvedCount}
            newCount={stats.newApprovedCount}
            color="green"
            isActive={activeFilter?.category === 'approved'}
            activeScope={activeFilter?.category === 'approved' ? activeFilter.scope : null}
            onClickAll={() => handleStatClick('approved', 'all')}
            onClickNew={() => handleStatClick('approved', 'new')}
          />
        </SimpleGrid>

        {/* Tabulator Table */}
        <Paper withBorder p="lg" radius="md">
          <Group justify="space-between" mb="md">
            <Group gap="sm">
              <Title order={4}>Documents ({stats.totalCount})</Title>
              {activeFilter && (
                <Tooltip label="Clear filter">
                  <Badge
                    size="lg"
                    variant="light"
                    color={activeFilter.scope === 'new' ? 'orange' : 'blue'}
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        color={activeFilter.scope === 'new' ? 'orange' : 'blue'}
                        onClick={() => setActiveFilter(null)}
                      >
                        <IconX size={12} />
                      </ActionIcon>
                    }
                  >
                    {activeFilter.category} ({activeFilter.scope})
                  </Badge>
                </Tooltip>
              )}
            </Group>
            <Group gap="xs">
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon variant="light" size="lg" title="Export Data">
                    <IconDownload size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Export Format</Menu.Label>
                  <Menu.Item onClick={() => handleExport('csv')}>Download CSV</Menu.Item>
                  <Menu.Item onClick={() => handleExport('json')}>Download JSON</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>

          {loading && !data.length && (
            <Center py="xl">
              <Stack align="center" gap="md">
                <Loader size="lg" />
                <Text c="dimmed">Loading documents...</Text>
              </Stack>
            </Center>
          )}
          <div ref={tableRef} style={loading && !data.length ? { display: 'none' } : undefined} />
        </Paper>
      </Stack>

      {/* Detail/PDF Drawer — opens on row click */}
      <Drawer
        opened={drawerOpened}
        onClose={() => { closeDrawer(); setPdfViewerOpen(false); }}
        position="right"
        size={pdfViewerOpen ? '80%' : 'lg'}
        title={
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="cyan">
              {isPdf(selectedItem ?? {} as PrimitiveItem) ? <IconFileTypePdf size={14} /> : <IconFileDescription size={14} />}
            </ThemeIcon>
            <Text fw={600}>{(selectedItem?.documentName as string) ?? (selectedItem?.name as string) ?? 'Document'}</Text>
            {selectedItem?.currentStatus && (
              <Badge size="sm" variant="light" color={STATUS_COLOR_MAP[selectedItem.currentStatus as string] ?? 'gray'}>
                {(selectedItem.currentStatus as string).replace(/_/g, ' ')}
              </Badge>
            )}
            {isPdf(selectedItem ?? {} as PrimitiveItem) && (
              <Badge size="xs" variant="light" color="violet">PDF</Badge>
            )}
          </Group>
        }
      >
        <Tabs value={drawerTab} onChange={setDrawerTab}>
          <Tabs.List>
            <Tabs.Tab value="details" leftSection={<IconEye size={14} />}>Details</Tabs.Tab>
            {isPdf(selectedItem ?? {} as PrimitiveItem) && (
              <Tabs.Tab value="pdf" leftSection={<IconFileTypePdf size={14} />}>PDF Viewer</Tabs.Tab>
            )}
          </Tabs.List>

          {/* Details Tab — uses shared formatFieldName + FieldValue (DRY) */}
          <Tabs.Panel value="details" pt="md">
            <ScrollArea h="calc(100vh - 200px)">
              <Stack gap="sm">
                {selectedItem && (
                  <>
                    <Table striped highlightOnHover withTableBorder>
                      <Table.Tbody>
                        {Object.entries(selectedItem)
                          .filter(([key]) => !key.startsWith('_') && key !== '__typename' && key !== 'dynamodbContent' && !INTERNAL_ID_FIELDS.has(key))
                          .map(([key, value]) => (
                            <Table.Tr key={key}>
                              <Table.Td fw={600} w={140} style={{ textTransform: 'capitalize' }}>
                                {formatFieldName(key)}
                              </Table.Td>
                              <Table.Td>
                                <FieldValue value={value} fieldKey={key} entity={selectedItem as Record<string, unknown>} />
                              </Table.Td>
                            </Table.Tr>
                          ))}
                      </Table.Tbody>
                    </Table>
                    {isPdf(selectedItem) && (
                      <>
                        <Divider my="sm" />
                        <Button
                          leftSection={<IconFileTypePdf size={16} />}
                          variant="light"
                          color="violet"
                          onClick={() => setDrawerTab('pdf')}
                        >
                          Open PDF Viewer
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>

          {/* PDF Viewer Tab (DRR-079) */}
          {isPdf(selectedItem ?? {} as PrimitiveItem) && (
            <Tabs.Panel value="pdf" pt="md">
              <ScrollArea h="calc(100vh - 200px)">
                <Stack gap="sm">
                  <Group gap="xs">
                    <Button
                      leftSection={<IconSignature size={16} />}
                      variant="light"
                      color="green"
                      size="xs"
                      onClick={openSignature}
                    >
                      Sign
                    </Button>
                    <Button
                      leftSection={<IconUsers size={16} />}
                      variant="light"
                      color="violet"
                      size="xs"
                      onClick={() => {
                        // Navigate to ceremony creation — document ID is set when a row is selected
                        if (docId) {
                          navigate(`/ferp-core/documents/ceremony/new?documentId=${docId}`);
                        }
                      }}
                    >
                      Start Ceremony
                    </Button>
                    <Button
                      leftSection={<IconDownload size={16} />}
                      variant="light"
                      size="xs"
                      onClick={exportPdf}
                    >
                      Export with Annotations
                    </Button>
                  </Group>

                  <PDFViewer
                    url={pdfUrl || `/test-document.pdf`}
                    documentId={docId as string}
                    annotations={annotations}
                    onAnnotationsChange={handleAnnotationsChange}
                    onRequestSign={openSignature}
                    onRequestExport={exportPdf}
                    canAnnotate
                    canSign
                  />
                </Stack>
              </ScrollArea>
            </Tabs.Panel>
          )}
        </Tabs>
      </Drawer>

      {/* Signature Capture Modal (DRR-079) */}
      <SignatureCapture
        opened={signatureOpened}
        onClose={closeSignature}
        onSign={handleSign}
        documentName={(selectedItem?.documentName as string) ?? (selectedItem?.name as string) ?? 'document'}
      />

      {/* PDF Generation Modal (DRR-232 Track I) */}
      <Modal
        opened={pdfModalOpened}
        onClose={closePdfModal}
        title="Generate PDF Document"
        size="lg"
      >
        <Stack gap="md">
          <Select
            label="PDF Template"
            placeholder="Select a template..."
            data={templateOptions}
            value={pdfFormState.templateId}
            onChange={(val) => setPdfFormState((s) => ({ ...s, templateId: val ?? '' }))}
            searchable
            required
          />

          <TextInput
            label="Document Name"
            placeholder="e.g., Admission Agreement — John Doe — 2026-04-11"
            value={pdfFormState.documentName}
            onChange={(e) => setPdfFormState((s) => ({ ...s, documentName: e.currentTarget.value }))}
            required
          />

          <Select
            label="Workflow Instance"
            placeholder="Link to a workflow instance..."
            data={instanceOptions}
            value={pdfFormState.workflowInstanceId}
            onChange={(val) => setPdfFormState((s) => ({ ...s, workflowInstanceId: val ?? '' }))}
            searchable
            required
          />

          <MultiSelect
            label="Regulatory Rules (optional)"
            placeholder="Select rules this document satisfies..."
            data={regulatoryRuleOptions}
            value={pdfFormState.regulatoryRuleIds}
            onChange={(val) => setPdfFormState((s) => ({ ...s, regulatoryRuleIds: val }))}
            searchable
            clearable
          />

          <Textarea
            label="Template Data (JSON)"
            placeholder='{"residentName": "John Doe", "facilityName": "Hillside Harmony"}'
            value={pdfFormState.dataContextJson}
            onChange={(e) => setPdfFormState((s) => ({ ...s, dataContextJson: e.currentTarget.value }))}
            minRows={4}
            autosize
          />

          {pdfError && (
            <Alert color="red" title="Generation Failed">
              {pdfError.message}
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closePdfModal}>
              Cancel
            </Button>
            <Button
              onClick={handleGeneratePdf}
              loading={generatingPdf}
              disabled={!pdfFormState.templateId || !pdfFormState.documentName || !pdfFormState.workflowInstanceId}
            >
              Generate PDF
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default DocumentManager;
